/**
 * Achievement engine — pure function that takes a text-features snapshot
 * and the set of already-earned achievement ids, and returns the achievements
 * that JUST earned (i.e., are met by features but not in the earned set).
 *
 * No async, no state, no side effects. Safe to call on every keystroke
 * (though in practice the caller debounces).
 */

import { Achievement, ACHIEVEMENT_CATALOG } from './catalog';
import { TextFeatures } from './analyzer';

export interface EngineResult {
  /** Achievements that JUST earned in this evaluation. */
  newlyEarned: Achievement[];
  /** Updated set of earned ids (caller persists this). */
  earned: Set<string>;
}

/**
 * Evaluate the catalog against features.
 *
 * @param features    output of analyze()
 * @param alreadyEarned   set of achievement ids the user has already earned
 * @returns           newly-earned achievements + updated earned set
 */
export function evaluate(
  features: TextFeatures,
  alreadyEarned: Set<string>,
): EngineResult {
  const newlyEarned: Achievement[] = [];
  const earned = new Set(alreadyEarned);

  for (const ach of ACHIEVEMENT_CATALOG) {
    if (earned.has(ach.id)) continue;
    if (ach.condition(features)) {
      newlyEarned.push(ach);
      earned.add(ach.id);
    }
  }

  return { newlyEarned, earned };
}

/**
 * Helper: list all achievements with their current state (earned/locked/progress).
 * Useful for rendering a "Trophy Case"-style display.
 */
export function snapshot(
  features: TextFeatures,
  earned: Set<string>,
): Array<{
  achievement: Achievement;
  isEarned: boolean;
  progress: { current: number; target: number } | null;
}> {
  return ACHIEVEMENT_CATALOG.map((ach) => ({
    achievement: ach,
    isEarned: earned.has(ach.id),
    progress: ach.progress ? ach.progress(features) : null,
  }));
}
